import React, { useMemo } from "react";
import { useProductDetailsMaster } from "../hooks/useProductDetailsMaster";
import "../assets/styles/grn_manifest_list.scss";

function pick(obj, snake, camel) {
  if (!obj) return null;
  if (obj[snake] != null && String(obj[snake]).trim() !== "") return obj[snake];
  if (camel && obj[camel] != null && String(obj[camel]).trim() !== "") return obj[camel];
  return null;
}

function formatDate(dateString) {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "N/A";
  }
}

function formatDateYmd(dateString) {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "N/A";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  } catch {
    return "N/A";
  }
}

const followUpActionOptions = [
  { value: "RETURN_TO_SUPPLIER", label: "Return to Supplier" },
  { value: "REPLACEMENT_REQUIRED", label: "Replacement Required" },
  { value: "CREDIT_NOTE", label: "Issue Credit Note" },
  { value: "INVESTIGATE", label: "Investigate Further" },
  { value: "ACCEPT_WITH_DISCOUNT", label: "Accept with Discount" },
  { value: "OTHER", label: "Other" },
];

function followUpLabel(value) {
  if (!value) return null;
  const o = followUpActionOptions.find((x) => x.value === value);
  return o ? o.label : value;
}

function statusBadgeClass(status) {
  const s = (status || "").toLowerCase().trim();
  if (s === "approved") return "badge bg-success";
  if (s === "partially approved") return "badge bg-warning text-dark";
  if (s === "rejected") return "badge bg-danger";
  if (s === "pending approval") return "badge bg-info text-dark";
  return "badge bg-secondary";
}

function parseChecklist(raw) {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return {};
}

/**
 * Read-only GRN popup for logistics RFQ sent list (matches manifest GRN preview layout).
 * Optional `logisticsShowAcceptGrn` + `onLogisticsAcceptGrn`: provider inbox when the delivery process is on
 * `grn_accept` — opens GRN acceptance overlay (approve-list style).
 */
export default function LogisticsGrnDetailsModal({
  open,
  onClose,
  grn,
  loading,
  logisticsShowAcceptGrn = false,
  logisticsAcceptGrnLoading = false,
  onLogisticsAcceptGrn,
}) {
  const { attributeUnitOptions } = useProductDetailsMaster();

  const getUnitLabel = (unitValue) => {
    if (!unitValue) return "";
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return String(unitValue);
    const unitValueNum = Number.isNaN(Number(unitValue)) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find((opt) => {
      if (String(opt.value) === String(unitValue)) return true;
      if (unitValueNum !== null && !Number.isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    if (foundByValue) return foundByValue.label || String(unitValue);
    const foundByLabel = attributeUnitOptions.find(
      (opt) =>
        opt.label === String(unitValue) || opt.label?.toLowerCase() === String(unitValue).toLowerCase(),
    );
    if (foundByLabel) return foundByLabel.label;
    return String(unitValue);
  };

  const checklist = useMemo(() => parseChecklist(grn?.quality_inspection_checklist), [grn]);

  const detailsKind = useMemo(() => {
    const s = (grn?.status || "").toLowerCase().trim();
    if (s === "approved") return "approved";
    if (s === "partially approved") return "partially-approved";
    if (s === "rejected") return "rejected";
    return null;
  }, [grn]);

  const hasPo = useMemo(() => {
    const id = pick(grn, "purchase_order_id", "purchaseOrderId");
    return id != null && Number(id) > 0;
  }, [grn]);

  const poNumber = pick(grn, "po_number", "poNumber");
  const supplierEmail = pick(grn, "supplier_email", "supplierEmail");

  if (!open) return null;

  return (
    <div className="grn-manifest-list-modal-wrapper">
      <div className="grn-generate">
        <div className="grn-preview-modal-overlay" onClick={onClose} role="presentation">
          <div
            className="grn-preview-modal logistics-grn-details-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="logistics-grn-details-title"
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
          >
            <button type="button" className="grn-preview-modal-close" onClick={onClose} aria-label="Close GRN details">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>

            <div className="grn-preview-modal-header">
              <div>
                <h2 id="logistics-grn-details-title" className="grn-preview-modal-title">
                  GRN Details — {grn?.grn_number || "…"}
                </h2>
                <p className="grn-preview-modal-subtitle">Complete details of the Goods Received Note.</p>
              </div>
            </div>

            <div className="grn-preview-content">
              {loading ? (
                <div className="p-4 text-center text-muted">Loading GRN…</div>
              ) : !grn ? (
                <div className="p-4 text-center text-muted">Could not load GRN.</div>
              ) : (
                <>
                  <div className="logistics-grn-summary-strip">
                    <div className="logistics-grn-summary-cell">
                      <span className="logistics-grn-summary-label">GRN number</span>
                      <span className="logistics-grn-summary-value">{grn.grn_number || "N/A"}</span>
                    </div>
                    <div className="logistics-grn-summary-cell">
                      <span className="logistics-grn-summary-label">GRN date</span>
                      <span className="logistics-grn-summary-value">
                        {grn.grn_date ? formatDateYmd(grn.grn_date) : "N/A"}
                      </span>
                    </div>
                    {hasPo ? (
                      <div className="logistics-grn-summary-cell">
                        <span className="logistics-grn-summary-label">PO number</span>
                        <span className="logistics-grn-summary-value">{poNumber || "N/A"}</span>
                      </div>
                    ) : null}
                    <div className="logistics-grn-summary-cell">
                      <span className="logistics-grn-summary-label">Delivery challan</span>
                      <span className="logistics-grn-summary-value">{grn.delivery_challan_no || "—"}</span>
                    </div>
                    <div className="logistics-grn-summary-cell logistics-grn-summary-status">
                      <span className="logistics-grn-summary-label">Status</span>
                      <span className={statusBadgeClass(grn.status)}>{grn.status || "N/A"}</span>
                    </div>
                  </div>

                  <div className="grn-preview-info-grid">
                    <div className="grn-preview-card grn-preview-card-orange">
                      <h3 className="grn-preview-card-title grn-preview-card-title-orange">Supplier details</h3>
                      <div className="grn-preview-card-content logistics-grn-field-grid">
                        <div>
                          <p className="grn-preview-label">Supplier name</p>
                          <p className="grn-preview-value">{grn.supplier_name || "N/A"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">GSTIN</p>
                          <p className="grn-preview-value">{grn.supplier_gstin || "N/A"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">Email</p>
                          <p className="grn-preview-value">{supplierEmail || "—"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">Phone number</p>
                          <p className="grn-preview-value">{grn.phone || "—"}</p>
                        </div>
                        <div style={{ gridColumn: "1 / -1" }}>
                          <p className="grn-preview-label">Address</p>
                          <p className="grn-preview-value">{grn.address || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                    <div className="grn-preview-card grn-preview-card-purple">
                      <h3 className="grn-preview-card-title grn-preview-card-title-purple">Delivery information</h3>
                      <div className="grn-preview-card-content logistics-grn-field-grid">
                        <div>
                          <p className="grn-preview-label">Delivery challan</p>
                          <p className="grn-preview-value">{grn.delivery_challan_no || "N/A"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">Manifest number</p>
                          <p className="grn-preview-value">{grn.manifest_number || "N/A"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">Delivery date</p>
                          <p className="grn-preview-value">{grn.delivery_date ? formatDateYmd(grn.delivery_date) : "N/A"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">Vehicle</p>
                          <p className="grn-preview-value">{grn.vehicle_number || "N/A"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">Driver name</p>
                          <p className="grn-preview-value">{grn.driver_name || "N/A"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">Driver contact</p>
                          <p className="grn-preview-value">{grn.driver_contact || "N/A"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">LR number</p>
                          <p className="grn-preview-value">{grn.lr_number || "N/A"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">Transport</p>
                          <p className="grn-preview-value">{grn.transport_company || "N/A"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">Received by</p>
                          <p className="grn-preview-value">{grn.received_by_name || "Not specified"}</p>
                        </div>
                        <div>
                          <p className="grn-preview-label">Warehouse</p>
                          <p className="grn-preview-value">{grn.warehouse_location || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {grn.items && grn.items.length > 0 ? (
                    <div className="grn-preview-table-section">
                      <h3 className="mb-2 fw-semibold">Received items</h3>
                      <div className="grn-preview-table-wrapper">
                        <table className="grn-preview-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Description</th>
                              <th>HSN</th>
                              <th className="grn-text-center">UOM</th>
                              <th className="grn-text-right">Ordered</th>
                              <th className="grn-text-right">Received</th>
                              <th className="grn-text-right">Accepted</th>
                              <th className="grn-text-right">Rejected</th>
                              <th>Batch/Lot</th>
                              <th>Mfg date</th>
                              <th>Exp date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {grn.items.map((item, index) => (
                              <tr key={item.id || index}>
                                <td>{index + 1}</td>
                                <td>{item.description || "—"}</td>
                                <td>{item.hsn || "—"}</td>
                                <td className="grn-text-center">{getUnitLabel(item.uom) || "—"}</td>
                                <td className="grn-text-right">{item.qty_ordered ?? "0"}</td>
                                <td className="grn-text-right">{item.qty_received ?? "0"}</td>
                                <td className="grn-text-right grn-text-green">{item.qty_accepted ?? "0"}</td>
                                <td className="grn-text-right grn-text-red">{item.qty_rejected ?? "0"}</td>
                                <td>{item.batch_lot || "—"}</td>
                                <td>{item.mfg_date ? formatDate(item.mfg_date) : "—"}</td>
                                <td>{item.exp_date ? formatDate(item.exp_date) : "—"}</td>
                              </tr>
                            ))}
                            <tr className="grn-preview-table-total">
                              <td colSpan={1} />
                              <td className="grn-text-right">
                                <strong>Totals:</strong>
                              </td>
                              <td colSpan={2} />
                              <td className="grn-text-right">
                                <strong>
                                  {grn.items.reduce((sum, item) => sum + (parseFloat(item.qty_ordered) || 0), 0)}
                                </strong>
                              </td>
                              <td className="grn-text-right">
                                <strong>
                                  {grn.items.reduce((sum, item) => sum + (parseFloat(item.qty_received) || 0), 0)}
                                </strong>
                              </td>
                              <td className="grn-text-right grn-text-green">
                                <strong>
                                  {grn.items.reduce((sum, item) => sum + (parseFloat(item.qty_accepted) || 0), 0)}
                                </strong>
                              </td>
                              <td className="grn-text-right grn-text-red">
                                <strong>
                                  {grn.items.reduce((sum, item) => sum + (parseFloat(item.qty_rejected) || 0), 0)}
                                </strong>
                              </td>
                              <td colSpan={3} />
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}

                  <div className="grn-preview-card grn-preview-card-green">
                    <h3 className="grn-preview-card-title grn-preview-card-title-green">Quality inspection checklist</h3>
                    <div className="grn-preview-card-content">
                      {checklist.packagingIntact ? (
                        <p className="grn-check-verified">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="grn-check-icon">
                            <circle cx="12" cy="12" r="10" />
                            <path d="m9 12 2 2 4-4" />
                          </svg>
                          Packaging intact
                        </p>
                      ) : null}
                      {checklist.noVisibleDamage ? (
                        <p className="grn-check-verified">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="grn-check-icon">
                            <circle cx="12" cy="12" r="10" />
                            <path d="m9 12 2 2 4-4" />
                          </svg>
                          No visible damage
                        </p>
                      ) : null}
                      {checklist.meetsQualityStandards ? (
                        <p className="grn-check-verified">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="grn-check-icon">
                            <circle cx="12" cy="12" r="10" />
                            <path d="m9 12 2 2 4-4" />
                          </svg>
                          Quality standards met
                        </p>
                      ) : null}
                      {checklist.correctLabeling ? (
                        <p className="grn-check-verified">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="grn-check-icon">
                            <circle cx="12" cy="12" r="10" />
                            <path d="m9 12 2 2 4-4" />
                          </svg>
                          Correct labeling
                        </p>
                      ) : null}
                      {checklist.quantityMatches ? (
                        <p className="grn-check-verified">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="grn-check-icon">
                            <circle cx="12" cy="12" r="10" />
                            <path d="m9 12 2 2 4-4" />
                          </svg>
                          Quantity matches
                        </p>
                      ) : null}
                      {checklist.allDocumentsComplete ? (
                        <p className="grn-check-verified">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="grn-check-icon">
                            <circle cx="12" cy="12" r="10" />
                            <path d="m9 12 2 2 4-4" />
                          </svg>
                          Documents complete
                        </p>
                      ) : null}
                      {!checklist.packagingIntact &&
                      !checklist.noVisibleDamage &&
                      !checklist.meetsQualityStandards &&
                      !checklist.correctLabeling &&
                      !checklist.quantityMatches &&
                      !checklist.allDocumentsComplete ? (
                        <p className="grn-check-unverified">No checklist items recorded</p>
                      ) : null}
                      <div className="grn-preview-inspector-info mt-3">
                        <p>
                          <strong>Inspector:</strong>{" "}
                          {grn.inspector_name || "Not specified"}{" "}
                          {grn.inspection_date ? `(${formatDateYmd(grn.inspection_date)})` : ""}
                        </p>
                        {grn.defect_issue_description ? (
                          <p>
                            <strong>Defect description:</strong> {grn.defect_issue_description}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="grn-preview-card grn-preview-card-indigo">
                    <h3 className="grn-preview-card-title grn-preview-card-title-indigo">Physical verification</h3>
                    <div className="grn-preview-verification-grid">
                      <div>
                        <p className="grn-preview-label">Packages expected / received</p>
                        <p className="grn-preview-value">
                          {grn.packages_expected ?? "0"} / {grn.packages_received ?? "0"}
                        </p>
                      </div>
                      <div>
                        <p className="grn-preview-label">Weight expected / received (kg)</p>
                        <p className="grn-preview-value">
                          {grn.weight_expected_kg != null ? Number(grn.weight_expected_kg).toFixed(2) : "0.00"} /{" "}
                          {grn.weight_received_kg != null ? Number(grn.weight_received_kg).toFixed(2) : "0.00"}
                        </p>
                      </div>
                      <div>
                        <p className="grn-preview-label">Seal numbers</p>
                        <p className="grn-preview-value">{grn.seal_numbers || "—"}</p>
                      </div>
                      <div>
                        <p className="grn-preview-label">Seals verified</p>
                        <p className={grn.seals_verified ? "grn-check-verified mb-0" : "grn-check-unverified mb-0"}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="grn-check-icon">
                            <circle cx="12" cy="12" r="10" />
                            <path d="m9 12 2 2 4-4" />
                          </svg>
                          {grn.seals_verified ? "Seals verified" : "Not verified"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grn-preview-card grn-preview-card-red">
                    <h3 className="grn-preview-card-title grn-preview-card-title-red">Discrepancies &amp; issues</h3>
                    <div className="logistics-grn-discrepancy-stack">
                      <div className="logistics-grn-discrepancy-block">
                        <p className="grn-preview-label mb-1">Shortage details</p>
                        <p className="grn-preview-value mb-0">
                          {grn.shortage_detected
                            ? grn.additional_notes || "—"
                            : "No shortage reported"}
                        </p>
                      </div>
                      <div className="logistics-grn-discrepancy-block">
                        <p className="grn-preview-label mb-1">Damage details</p>
                        <p className="grn-preview-value mb-0">
                          {grn.damage_detected ? grn.additional_notes || "—" : "No damage reported"}
                        </p>
                      </div>
                      <div className="logistics-grn-discrepancy-block">
                        <p className="grn-preview-label mb-1">Quality issue</p>
                        <p className="grn-preview-value mb-0">
                          {grn.quality_issue ? grn.defect_issue_description || "—" : "No quality issue reported"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {grn.additional_notes ? (
                    <div className="grn-preview-card grn-preview-card-gray">
                      <h3 className="grn-preview-card-title grn-preview-card-title-gray">Additional notes</h3>
                      <div className="grn-preview-card-content">
                        <p className="mb-0">{grn.additional_notes}</p>
                      </div>
                    </div>
                  ) : null}

                  {detailsKind === "approved" ? (
                    <div className="grn-preview-card grn-preview-card-green">
                      <h3 className="grn-preview-card-title grn-preview-card-title-green">Approval details</h3>
                      <div className="grn-preview-card-content">
                        <div className="grn-preview-info-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                          <div>
                            <p className="grn-preview-label">Approved by</p>
                            <p className="grn-preview-value">{grn.approved_by || "N/A"}</p>
                          </div>
                          {grn.approved_by_designation ? (
                            <div>
                              <p className="grn-preview-label">Designation</p>
                              <p className="grn-preview-value">{grn.approved_by_designation}</p>
                            </div>
                          ) : (
                            <div />
                          )}
                          <div>
                            <p className="grn-preview-label">Approval date</p>
                            <p className="grn-preview-value">{formatDateYmd(grn.approved_date)}</p>
                          </div>
                          {grn.approval_notes ? (
                            <div style={{ gridColumn: "1 / -1" }}>
                              <p className="grn-preview-label">Approval notes</p>
                              <p className="grn-preview-value">{grn.approval_notes}</p>
                            </div>
                          ) : null}
                          {grn.follow_up_action ? (
                            <div style={{ gridColumn: "1 / -1" }}>
                              <p className="grn-preview-label">Follow-up action</p>
                              <p className="grn-preview-value">{followUpLabel(grn.follow_up_action)}</p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {detailsKind === "partially-approved" ? (
                    <div className="grn-preview-card grn-preview-card-yellow">
                      <h3 className="grn-preview-card-title grn-preview-card-title-yellow">Partial approval details</h3>
                      <div className="grn-preview-card-content">
                        <div className="grn-preview-info-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                          <div>
                            <p className="grn-preview-label">Approved by</p>
                            <p className="grn-preview-value">{grn.approved_by || "N/A"}</p>
                          </div>
                          {grn.approved_by_designation ? (
                            <div>
                              <p className="grn-preview-label">Designation</p>
                              <p className="grn-preview-value">{grn.approved_by_designation}</p>
                            </div>
                          ) : (
                            <div />
                          )}
                          <div>
                            <p className="grn-preview-label">Approval date</p>
                            <p className="grn-preview-value">{formatDateYmd(grn.approved_date)}</p>
                          </div>
                          {grn.approval_notes ? (
                            <div style={{ gridColumn: "1 / -1" }}>
                              <p className="grn-preview-label">Approval notes</p>
                              <p className="grn-preview-value">{grn.approval_notes}</p>
                            </div>
                          ) : null}
                          {grn.follow_up_action ? (
                            <div style={{ gridColumn: "1 / -1" }}>
                              <p className="grn-preview-label">Follow-up action</p>
                              <p className="grn-preview-value">{followUpLabel(grn.follow_up_action)}</p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {detailsKind === "rejected" ? (
                    <div className="grn-preview-card grn-preview-card-red">
                      <h3 className="grn-preview-card-title grn-preview-card-title-red">Rejection details</h3>
                      <div className="grn-preview-card-content">
                        <div className="grn-preview-info-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
                          <div>
                            <p className="grn-preview-label">Rejected by</p>
                            <p className="grn-preview-value">{grn.rejected_by || "N/A"}</p>
                          </div>
                          {grn.rejected_by_designation ? (
                            <div>
                              <p className="grn-preview-label">Designation</p>
                              <p className="grn-preview-value">{grn.rejected_by_designation}</p>
                            </div>
                          ) : null}
                          <div>
                            <p className="grn-preview-label">Rejected date</p>
                            <p className="grn-preview-value">{formatDateYmd(grn.rejected_date)}</p>
                          </div>
                          {grn.rejection_reason ? (
                            <div style={{ gridColumn: "1 / -1" }}>
                              <p className="grn-preview-label">Reason</p>
                              <p className="grn-preview-value">{grn.rejection_reason}</p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className="grn-preview-modal-footer d-flex flex-wrap gap-2 justify-content-end align-items-center">
              {logisticsShowAcceptGrn && typeof onLogisticsAcceptGrn === "function" ? (
                <button
                  type="button"
                  className="grn-btn grn-btn-primary"
                  onClick={onLogisticsAcceptGrn}
                  disabled={logisticsAcceptGrnLoading || loading}
                >
                  {logisticsAcceptGrnLoading ? "Saving…" : "Accept GRN"}
                </button>
              ) : null}
              <button type="button" className="grn-btn grn-btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
